import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthState, UserView } from '@vxh/shared';
import { api } from './api';

interface AuthValue {
  state: AuthState | undefined;
  user: UserView | null;
  isAdmin: boolean;
  refresh: () => Promise<unknown>;
}

const Ctx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  // 'always': the session cookie is shared by every tab, so a tab that was left behind (the other tab signed in, signed out or passed the
  // authenticator step) asks again the moment it is looked at, instead of showing a stale form for up to half a minute.
  const q = useQuery({ queryKey: ['auth'], queryFn: () => api<AuthState>('/auth/me'), staleTime: 30_000, refetchOnWindowFocus: 'always' });
  const mark = q.data ? `${q.data.state}:${q.data.state === 'authed' ? q.data.user.id : ''}` : '';

  // Tell the other tabs when this one learns that the sign-in state changed (they ask the server; nothing secret is sent).
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('vxh-auth');
    ch.onmessage = () => void qc.invalidateQueries({ queryKey: ['auth'] });
    if (mark) ch.postMessage(mark);
    return () => ch.close();
  }, [mark, qc]);
  const user = q.data?.state === 'authed' ? q.data.user : null;
  const value: AuthValue = { state: q.data, user, isAdmin: user?.role === 'ADMIN', refresh: () => qc.invalidateQueries() };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
